import React, { useRef, useState, useEffect } from 'react';
import Panel from './Panel';
import { DropDownList } from './DropDownList';
import { TriangleIconRight } from '../Iconset';
import { LayoutOrientations } from '../../globals';
import { KeyCodes } from '../../interfaces/Input';
import DropDownLayer from '../layers/DropdownLayer';
import DomUtils from '../../classes/utilities/DomUtils';
import { IDomRect, IDropdownOption } from '../../interfaces/Misc';
import { deepEqual } from '../../classes/utilities/Misc';

interface ICustomDropdownProps
{
	value: any | any[],
	placeholder?: string,
	searchable?: boolean,
	multivalues?: boolean,
	multiSelect?: boolean,
	options: IDropdownOption[],
	style?: React.CSSProperties,
	decorators?: React.ReactNode,
	readOnly?: boolean,
	className?: string,
	noWidthConstraint?: boolean,
	noMaxWidth?: boolean,
	onChangeCallback?: (value: any) => void,
	faceValue?: React.ReactNode,
	dropdownClass?: string,
}

/**
 * the interactive portion of a DropdownProperty
 *
 * we replace the native browser dropdown list so we can do custom and complex drawing for list entries
 * @export
 * @returns a button that, when clicked, will display the provided options in a scrollable list
 */
export default function CustomDropdown(props: ICustomDropdownProps)
{
	const valueDisplay = buildValueDisplay();
	const faceValue =
		props.multivalues ? "(multiple values...)" :
			props.faceValue ? props.faceValue :
				valueDisplay ? valueDisplay :
					props.placeholder ? <i>{props.placeholder}</i> :
						<i>{"Select Option"}</i>;

	const rootRef = useRef<HTMLDivElement>(null);
	const wrapperRef = useRef(null);

	const [listVisible, setListVisible] = useState(false);
	const contentHeightLimit = useRef(200);
	const rootRect = useRef<IDomRect>();
	const drawParams = useRef<any>(null);

	useEffect(() =>
	{
		return () =>
		{
			closeList();
		}
	}, []);

	function getOptionDisplayFromValue(value)
	{
		for (const option of props.options)
		{
			if (deepEqual(option.value, value))
				return option.display;
		}

		return null;
	}


	function buildValueDisplay()
	{
		if (!props.options)
			return null;

		if (Array.isArray(props.value))
		{
			const filtered = props.value.map(val =>
			{
				const valDisplay = getOptionDisplayFromValue(val);
				if (valDisplay)
					return valDisplay;
				else return null;
			}).filter(v => v !== null);

			if (filtered.length > 1)
			{
				return "(" + filtered.join(", ") + ")";
			}
			else return filtered[0];
		}

		return getOptionDisplayFromValue(props.value);
	}

	function testClickOut(e: MouseEvent)
	{
		if(e.target instanceof HTMLElement)
		{
			const nearestRoot = e.target.closest(".dropDownListWrapper");
			if (nearestRoot !== wrapperRef.current)
			{
				closeList();
			}
		}
	}

	function testWheelOut(e: WheelEvent)
	{
		if(e.target instanceof HTMLElement)
		{
			const nearestRoot = e.target.closest(".dropDownListWrapper");
			if (wrapperRef.current && nearestRoot !== wrapperRef.current)
			{
				closeList();
			}
		}
	}

	function testKeyOut(e: KeyboardEvent)
	{
		if (e.keyCode === KeyCodes.ESCAPE)
		{
			closeList();
		}
	}

	function closeList()
	{
		document.removeEventListener('mousedown', testClickOut);
		document.removeEventListener('keyup', testKeyOut);
		document.removeEventListener('wheel', testWheelOut);
		setListVisible(false);
		DropDownLayer.setDropdownList(null);
	}

	function buildDropdownList(className: string = '')
	{
		if (!rootRect.current) return;

		return <div className={"dropDownListWrapper "+ className}
			ref={wrapperRef}
			data-name={"drop-down-list-wrapper"}
			style={{
				...drawParams.current,
				minWidth: (props.noWidthConstraint ? "fit-content" : drawParams.current.width),
			}}
		>
			<DropDownList
				data-name={props["data-name"] + "-options-container"}
				searchable={props.searchable}
				options={props.options}
				selected={props.value}
				multiSelect={props.multiSelect}
				onSelectionChanged={(selection) =>
				{
					if (props.onChangeCallback)
					{
						props.onChangeCallback(selection);
					}

					if (!props.multiSelect)
					{
						closeList();
					}
				}}
				maxHeight={contentHeightLimit.current}
				decorators={props.decorators}
				inverse={drawParams.current ? drawParams.current.inverse ? drawParams.current.inverse : false : false}
			/>
		</div>
	}

	useEffect(() =>
	{
		if (!listVisible) return;
		const dropdownList = buildDropdownList(props.dropdownClass);
		if (dropdownList)
		{
			DropDownLayer.setDropdownList(dropdownList);
		}
	}, [props.options]);

	return (
		<div data-name={props["data-name"] + "-options-root"}
			className={["dropDownRoot",
				(props.className ?? ""),
			].join(' ')}
			data-disabled={props.readOnly ? "true" : "false"}
			style={props.style ?? {}}
			ref={rootRef}
			data-status={listVisible ? "opened" : "none"}
		>
			<button className={"dropDownButton"}
				data-name={props["data-name"] + "-value-display-button"}
				disabled={props.readOnly}
				onKeyPress={(e) =>
				{
					e.preventDefault();
				}}
				onClick={(e) =>
				{
					if (listVisible)
					{
						closeList();
					}
					else
					{
						/*
							here we display the dropdown list. we need to calculate the position of the list based on the position of the root element
							we also need to take into account the scroll position of the root element's container
							and we need to limit the height of the list to the available space below the root element, up to the yMax value, which is the bottom of the workspace area
							finally, we subscribe to various events to close the list when the user clicks outside of it, presses the escape key, or scrolls outside of it
							this should mimic the behavior of a native dropdown list
						*/

						if(!rootRef.current) return;
						
						const scrollOffset = DomUtils.getScrollOffsets(rootRef.current.closest('.listViewContainer') as HTMLElement);
						const rRect = DomUtils.getDomElementRect(rootRef.current);
						if(!rRect) return;

						rootRect.current = { ...rRect };
						rootRect.current.bottom -= scrollOffset.y;
						rootRect.current.left -= scrollOffset.x;

						let contentMinimumHeight = Math.min(3, props.options.length) * 20;
						if (props.searchable)
							contentMinimumHeight += 20;
						if (props.decorators)
							contentMinimumHeight += 20;

						const bottomOfWorkspaceArea = DomUtils.getDomElementRect(document.getElementById('workspaceRoot') as HTMLElement)?.yMax ?? window.innerHeight;
						contentHeightLimit.current = Math.min(bottomOfWorkspaceArea - rootRect.current.yMax, 300);

						drawParams.current = {
							left: (rootRect.current.left) + "px",
							width: (rootRect.current.width) + "px",
						};

						if (rootRect.current.bottom + contentMinimumHeight > bottomOfWorkspaceArea)
						{
							drawParams.current.inverse = true;
							drawParams.current.top = (rootRect.current.bottom + 2 - rootRect.current.height - contentMinimumHeight) + "px";
							contentHeightLimit.current = contentMinimumHeight;
						}
						else
						{
							drawParams.current.top = (rootRect.current.bottom) + "px";
						}

						const dropdownList = buildDropdownList(props.dropdownClass);

						document.addEventListener('mousedown', testClickOut);
						document.addEventListener('keyup', testKeyOut);
						document.addEventListener('wheel', testWheelOut);
						setListVisible(true);

						if (dropdownList)
						{
							DropDownLayer.setDropdownList(dropdownList);
						}
					}
				}}
			>
				<div className={"dropDownValueDisplay"}>
					<div className={props.noMaxWidth ? "dropDownValueLabelNoMax" : "dropDownValueLabel"}>
						{
							<Panel orientation={LayoutOrientations.Horizontal} 
								style={{ 
									overflow: 'hidden', 
									alignItems: 'center',
									opacity: (props.options[props.value]?.disabled ? " 0.5" : "1"),
								}}
							>
								{faceValue}
							</Panel>
						}
					</div>
					<TriangleIconRight rotate={90} scale={0.8} />
				</div>
			</button>
		</div>
	);
}